import { QueryClient, QueryFunction } from "@tanstack/react-query";

let csrfToken: string | null = null;

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

export function getCsrfHeaders(method: string): Record<string, string> {
  const upper = method.toUpperCase();
  if (upper === "GET" || upper === "HEAD" || upper === "OPTIONS") {
    return {};
  }
  return { "X-CSRF-Token": csrfToken ?? "" };
}

export async function fetchFreshCsrfToken(): Promise<void> {
  try {
    const res = await fetch("/api/csrf-token", { credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      if (data?.csrfToken) {
        setCsrfToken(data.csrfToken);
      }
    }
  } catch {
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    if (res.status === 403) {
      try {
        const parsed = JSON.parse(text);
        if (parsed.suspended) {
          window.location.href = "/login?suspended=true&reason=" + encodeURIComponent(parsed.reason || "");
          throw new Error("Account suspended");
        }
      } catch (e) {
        if (e instanceof Error && e.message === "Account suspended") throw e;
      }
    }
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const makeRequest = () =>
    fetch(url, {
      method,
      headers: {
        ...getCsrfHeaders(method),
        ...(data ? { "Content-Type": "application/json" } : {}),
      },
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

  let res = await makeRequest();

  if (res.status === 403 && method.toUpperCase() !== "GET") {
    try {
      const clone = res.clone();
      const text = await clone.text();
      const parsed = JSON.parse(text);
      if (parsed?.message === "invalid csrf token") {
        await fetchFreshCsrfToken();
        res = await makeRequest();
      }
    } catch {
    }
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
